import { getItemLocalMediaStatesWith, syncClipWithDownload, tryMakeDownloadIdentityForClipInfo } from '@mimir/client-common/upload/premiere';
import { vueSet } from '../compat/vueCompat';
import { logErrorAndShowToast } from '../util/uiError';
import { clipInfosArray, updatePremiereClipInfo } from './premiere';
import { startPollingTransferProgress } from './transferProgress';
import { makeDownloadOperation } from '../premiere/makeSyncOp';
import type { AdobePanelContext, ElectronContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';
import { storeDispatchLoadStorageProfile } from '@mimir/web/desktop/storageProfile';
import type { DownloadAndRelinkOps } from '@mimir/client-common/fileSync/downloadAndRelinkOps';
import { invariant } from '@mimir/common/util/invariant';


export async function mediaRestored(
  services: {
    applicationContext: AdobePanelContext | ElectronContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps,
    downloadAndRelinkOps: DownloadAndRelinkOps,
  },
  config: {
    downloadPath: string,
    isStandaloneMode: boolean,
    isProxyMode: boolean,
    userDataSystemPath: string,
    shouldUsePremierePreferences: boolean,
    skippedFileExtensions: string[],
  },
  itemId: string,
) {
  const queuedDownload = services.ongoingDownloadOps.getOngoingDownload(itemId);

  if (queuedDownload) {
    try {
      queuedDownload.highresIsOffline = false;
      vueSet(queuedDownload, 'restoreStatus', undefined);
      services.ongoingDownloadOps.updateOngoingDownload(queuedDownload);
      const clipInfo = clipInfosArray.value.find((c) => c && c.mimirItemId === itemId);
      // Sync ClipInfo of the corresponding item
      if (clipInfo && clipInfo.mimirItem) {
        const dlIdHighres = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
        invariant(dlIdHighres !== null, 'Unexpected null dlIdHighres since clipInfo.mimirItem is defined');
        const dlIdProxy = tryMakeDownloadIdentityForClipInfo('proxy', clipInfo);
        invariant(dlIdProxy !== null, 'Unexpected null dlIdProxy since clipInfo.mimirItem is defined');
        clipInfo.mimirItemLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlIdHighres, dlIdProxy);
        updatePremiereClipInfo(clipInfo);
      }

      const profile = await storeDispatchLoadStorageProfile(
        services.applicationContext,
        false,
        config.downloadPath,
        services.localStateOps,
      );
      if (!profile) {
        // User canceled
        return;
      }

      if (profile.storageId) {
        startPollingTransferProgress(services, profile.storageId);
      }
    } catch (err) {
      logErrorAndShowToast('Could not restore media', err, 'ex in mediaRestored');
    }
  } else {
    services.logger.info(`Item ${itemId} has been restored without a transfer request`);
    const clipInfo = clipInfosArray.value.find((c) => c && c.mimirItemId === itemId);
    // Sync ClipInfo of the corresponding item
    if (clipInfo && clipInfo.mimirItem) {
      services.logger.info(`Initiating import of highres for restored item ${itemId}`);
      const dlIdHighres = tryMakeDownloadIdentityForClipInfo('highres', clipInfo);
      invariant(dlIdHighres !== null, 'Unexpected null dlIdHighres since clipInfo.mimirItem is defined');
      const dlIdProxy = tryMakeDownloadIdentityForClipInfo('proxy', clipInfo);
      invariant(dlIdProxy !== null, 'Unexpected null dlIdProxy since clipInfo.mimirItem is defined');
      clipInfo.mimirItemLocalMediaStates = await getItemLocalMediaStatesWith(services.localStateOps, clipInfo.mimirItem, null, dlIdHighres, dlIdProxy);
      updatePremiereClipInfo(clipInfo);
      const dlOp = makeDownloadOperation(config, 'highres', false);
      await syncClipWithDownload(services, clipInfo, dlOp, null, undefined);
    }
  }
}
